import { EllipsisVertical } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "src/components/buttons/shadcn-button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/Popover";
import { Employee } from "src/redux/types/_employee.type";
import EmployeeDetailsDialogue from "./EmployeeDetailsDialogue";
import GrantAccessDialog from "./GrantAccessDialog";
import { toast } from "react-toastify";
import { archiveEmployee } from "src/redux/api/_hr.api";

interface EmployeeListRowProps {
  emp: Employee;
  key: number;
  edit: (emp: Employee) => void;
}

const EmployeeListRow = ({ emp, key, edit }: EmployeeListRowProps) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const handleArchive = async (id: number) => {
    setLoading(true);
    try {
      const res = await archiveEmployee(id);

      if(res.message.toLowerCase() === "success") {
        toast.success("Employee Archived!");
      }

    } catch (error: any) {
      const errorCode = error?.response?.data?.data?.code;

      if (errorCode === "UserNotFoundException") {
        toast.success("Employee Archived!");
      } else {
        toast.error("Failed to Archive Employee!");
        console.error(error);
      }
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <tr
      key={key}
      className="border-b last:border-0 hover:bg-gray-50 transition text-xs"
    >
      <td className="py-1 uppercase">{emp.employee_code}</td>
      <td className="py-1 flex flex-col">
        <div>
          <p className="font-medium uppercase">{emp.full_name}</p>
          <p className="text-gray-600">{emp.email}</p>
          <p className="text-gray-600">{emp.phone_number}</p>
        </div>
      </td>
      <td className="py-1 uppercase">{emp.department}</td>
      <td className="py-1 uppercase">{emp.designation}</td>
      <td className="py-1 uppercase">{emp.reporting_manager_name}</td>
      <td className="py-1 uppercase">
        <span
          className={`transition-colors duration-200 flex flex-wrap w-fit ${
            emp.employeement_type === "CONFIRMED"
              ? "text-green-700"
              : "text-gray-700"
          }`}
        >
          {emp.employeement_type}
        </span>
      </td>
      <td className="py-1 uppercase flex flex-col">
        <div className="text-center">
          <p className="font-medium uppercase">{emp.employeement_type === "CONFIRMED" ? "Starting From" :  emp.duration}</p>
          {emp.employeement_type === "CONFIRMED" ? (
            <p className="text-gray-600">
              {emp.end_date}
            </p>
          ) : (
            <p className="text-gray-600">
              {emp.start_date} to {emp.end_date ? emp.end_date : "--"}
            </p>
          )}
        </div>
      </td>
      <td>
        <Popover>
          <PopoverTrigger asChild>
            <button className="p-2 hover:bg-slate-100 rounded-full">
              <EllipsisVertical className="w-5 h-5 text-slate-600 hover:text-primary" />
            </button>
          </PopoverTrigger>

          <PopoverContent className="w-fit p-2 space-y-2 flex flex-col">
            <Button
              className="w-fit justify-center"
              onClick={() =>
                navigate("/hr/payroll", { state: { employee: emp } })
              }
            >
              + Add Payroll
            </Button>
            <EmployeeDetailsDialogue employee={emp}>
              <Button className="w-full justify-center">View</Button>
            </EmployeeDetailsDialogue>
            <GrantAccessDialog full_name={emp.full_name} id={emp.id}>
              <Button className="w-full">Access</Button>
            </GrantAccessDialog>
            <Button className="w-full justify-center" onClick={() => edit(emp)}>
              Edit
            </Button>
            <Button
              className="w-full justify-center"
              onClick={() => handleArchive(emp.id)}
            >
              {loading ? (
                <span className="loading loading-dots loading-md"></span>
              ) : (
                "Archive"
              )}
            </Button>
          </PopoverContent>
        </Popover>
      </td>
    </tr>
  );
};

export default EmployeeListRow;
